import { z } from 'zod';
import { OperationIdSchema, PlayerIdSchema } from '../../ids';
import {
  CompanyBannerSchema,
  CompanyIdSchema,
  CompanyInviteSchema,
  CompanyNameSchema,
  CompanyRankSchema,
  CompanySchema,
} from '../../models/company';
import { PositiveQuantitySchema } from '../../models/items';
import { CharacterNameSchema } from '../../models/player';
import { API_PREFIX, defineEndpoint } from '../endpoint';

/** What every company command answers: the character's company (if any) and its invitations. */
export const CompanyStateSchema = z.object({
  company: CompanySchema.nullable(),
  invites: z.array(CompanyInviteSchema),
});
export type CompanyState = z.infer<typeof CompanyStateSchema>;

/** The character's company and the invitations standing for it. */
export const getCompany = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/company`,
  access: 'session',
  response: CompanyStateSchema,
});

/** Founds a company, paying its cost (a sink); the founder leads it. Idempotent. */
export const foundCompany = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/company`,
  access: 'session',
  body: z.object({
    operationId: OperationIdSchema,
    name: CompanyNameSchema,
    banner: CompanyBannerSchema,
  }),
  response: CompanyStateSchema,
});

/** Invites a character by name (the rank must allow it); it stands for a while. */
export const inviteToCompany = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/company/invites`,
  access: 'session',
  body: z.object({ name: CharacterNameSchema }),
  response: CompanyStateSchema,
});

/** Accepts or declines a standing invitation; accepting joins at the lowest rank. */
export const answerCompanyInvite = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/company/invites/:companyId/:answer`,
  access: 'session',
  params: z.object({ companyId: CompanyIdSchema, answer: z.enum(['accept', 'decline']) }),
  response: CompanyStateSchema,
});

/** Leaves the company (a leader names another first unless alone; the last one dissolves it). */
export const leaveCompany = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/company/leave`,
  access: 'session',
  response: CompanyStateSchema,
});

/** Expels a member of a lower rank (the rank must allow it). */
export const expelFromCompany = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/company/members/:playerId/expel`,
  access: 'session',
  params: z.object({ playerId: PlayerIdSchema }),
  response: CompanyStateSchema,
});

/**
 * Sets a lower member's rank, below one's own; the leader naming a new leader steps down to
 * officer (always one leader).
 */
export const setCompanyRank = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/company/members/:playerId/rank`,
  access: 'session',
  params: z.object({ playerId: PlayerIdSchema }),
  body: z.object({ rank: CompanyRankSchema }),
  response: CompanyStateSchema,
});

/**
 * Moves coins between the character's treasury and the company's: any member deposits;
 * withdrawing needs the rank and the seniority (GR-4). Idempotent.
 */
export const moveCompanyCoins = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/company/treasury/:side`,
  access: 'session',
  params: z.object({ side: z.enum(['deposit', 'withdraw']) }),
  body: z.object({ operationId: OperationIdSchema, amount: PositiveQuantitySchema }),
  response: CompanyStateSchema,
});
